/**
 * The live preview: how the newest real leads would score, as a histogram
 * coloured by band, the count in each band, how often each rule matched, and
 * the highest and lowest examples.
 *
 * Read-only on the server, and only ever over real leads (never a reference
 * list). Used beside the rules list (the saved rules) and inside the rule
 * editor (the rules with the draft standing in for the one being edited).
 */
import {
  Alert, Badge, Group, Loader, SimpleGrid, Stack, Table, Text,
} from '@mantine/core';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ScorePreviewResponse, ScoringSettings } from '../../api/scoring';
import { BAR_CURSOR, GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS } from '../../charts';
import { formatNumber } from '../../theme';
import { ChartTooltip, MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import { ScoreBadge, signed } from './scoringShared';

/** The band a histogram bar starts in, for its colour. */
function bandAt(settings: ScoringSettings | undefined, score: number) {
  const bands = settings?.bands ?? [];
  let found = bands[0];
  for (const b of bands) {
    if (b.min == null || score >= b.min) found = b;
  }
  return found;
}

export function ScorePreview({
  data,
  loading,
  error,
  settings,
  highlightRuleId,
  compact = false,
}: {
  data: ScorePreviewResponse | undefined;
  loading: boolean;
  error: Error | null;
  settings: ScoringSettings | undefined;
  /** The rule being edited, picked out in the rule list. */
  highlightRuleId?: string;
  compact?: boolean;
}) {
  const v = useVocab();
  if (error) return <Alert color="red" variant="light">{error.message}</Alert>;
  if (!data) {
    return <Group justify="center" p="lg">{loading ? <Loader size="sm" /> : <Text size="sm" c="dimmed">No preview yet.</Text>}</Group>;
  }
  if (data.sampled === 0) {
    return <Text size="sm" c="dimmed">{`No recent ${v.many('lead')} to score yet.`}</Text>;
  }
  const rows = data.histogram.map((b) => ({
    label: b.from === b.to ? `${b.from}` : `${b.from} to ${b.to}`,
    count: b.count,
    colour: `var(--mantine-color-${bandAt(settings, b.from)?.colour ?? 'gray'}-6)`,
  }));
  return (
    <Stack gap="sm" data-testid="score-preview">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
        <Text size="xs" c="dimmed">
          {`The newest ${formatNumber(data.sampled)} ${v.word(data.sampled, 'lead')}, scored now. Nothing is saved.`}
        </Text>
        <Group gap={6}>
          {loading && <Loader size={12} />}
          <MonoLabel>{`Median ${data.median} · Average ${data.average}`}</MonoLabel>
        </Group>
      </Group>
      <div role="img" aria-label={`Scores from ${data.min} to ${data.max}, median ${data.median}.`}>
        <ResponsiveContainer width="100%" height={compact ? 140 : 180}>
          <BarChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: -4 }} barCategoryGap="14%">
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" {...X_AXIS_PROPS} interval="preserveStartEnd" minTickGap={12} />
            <YAxis {...Y_AXIS_PROPS} allowDecimals={false} />
            <Tooltip
              cursor={BAR_CURSOR}
              isAnimationActive={false}
              content={<ChartTooltip names={{ count: v.Many('lead') }} formatters={{ count: formatNumber }} />}
            />
            <Bar dataKey="count" name={v.Many('lead')} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {rows.map((r) => <Cell key={r.label} fill={r.colour} fillOpacity={0.85} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Group gap="xs" wrap="wrap" data-testid="band-counts">
        {data.bands.map((b) => (
          <Badge key={b.key} variant="light" color={b.colour || 'gray'} radius="sm" tt="none" size="md">
            {`${b.label}: ${formatNumber(b.count)}`}
          </Badge>
        ))}
      </Group>
      {data.errors.length > 0 && (
        <Alert color="chartAmber" variant="light" title="Some rules could not be run">
          <Stack gap={2}>
            {data.errors.map((e) => (
              <Text key={e.rule_id} size="sm">{`${e.name}: ${e.reason} It adds 0.`}</Text>
            ))}
          </Stack>
        </Alert>
      )}
      {!compact && data.rules.length > 0 && (
        <Table verticalSpacing={4} horizontalSpacing="xs" fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Rule</Table.Th>
              <Table.Th ta="right">Points</Table.Th>
              <Table.Th ta="right">Matched</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.rules.map((r) => (
              <Table.Tr key={r.rule_id} fw={r.rule_id === highlightRuleId ? 600 : undefined}>
                <Table.Td>{r.name}</Table.Td>
                <Table.Td ta="right">{signed(r.points)}</Table.Td>
                <Table.Td ta="right">
                  {`${formatNumber(r.matched)} of ${formatNumber(data.sampled)}`}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Examples title="Highest" rows={data.top} />
        <Examples title="Lowest" rows={data.bottom} />
      </SimpleGrid>
    </Stack>
  );
}

function Examples({ title, rows }: { title: string; rows: ScorePreviewResponse['top'] }) {
  return (
    <Stack gap={4}>
      <MonoLabel>{title}</MonoLabel>
      {rows.map((r) => (
        <Group key={r.lead_id} gap="xs" wrap="nowrap" justify="space-between">
          <div style={{ minWidth: 0 }}>
            <Text size="sm" truncate="end">{r.name || r.email || r.lead_id}</Text>
            <Text size="xs" c="dimmed" truncate="end">
              {r.matched.length ? r.matched.map((m) => `${signed(m.points)} ${m.name}`).join(', ') : 'No rule matched'}
            </Text>
          </div>
          <div style={{ flexShrink: 0 }}>
            <ScoreBadge score={r.score} band={r.band} />
          </div>
        </Group>
      ))}
    </Stack>
  );
}
